import React, { useContext, useEffect, useMemo, useState } from 'react';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import styles from '../../styles/pages/Analytics/AnalyticsPage.module.css';
import { Title } from '../../components/UI/Custom/Title';
import { LoadingCircle } from '../../components/UI/Custom/LoadingCircle';
import { DropdownSelector } from '../../components/UI/DropdownSelector';
import { MultiSelectDropdown } from '../../components/UI/MultiSelectDropdown';
import { InputSelector } from '../../components/UI/InputSelector';
import { ThemeContext } from '../../context/ThemeContext';
import { LangContext } from '../../context/LangContext';
import { getMyAccountApi } from '../../utils/api/Account/getMyAccountApi';
import { getLeadAnalyticsApi } from '../../utils/api/Analytics/getLeadAnalyticsApi';

const translations = {
    ru: {
        title: 'Аналитика',
        accessDenied: 'Доступ к аналитике отсутствует',
        loadError: 'Не удалось загрузить аналитику.',
        apply: 'Применить',
        reset: 'Сбросить',
        all: 'Все',
        lead: 'Лид',
        manager: 'Менеджер',
        desk: 'Desk',
        from: 'С даты',
        to: 'По дату',
        quickRanges: {
            sevenDays: 'Последние 7 дней',
            oneMonth: 'Последний месяц',
            threeMonths: 'Последние 3 месяца',
        },
        empty: 'Недостаточно данных за выбранный период.',
        currentSlice: 'Текущий срез',
        dateRange: 'Период',
        scope: 'Фильтры',
        statusBreakdown: 'Статусы',
        filtersHint: 'Фильтруйте аналитику по desk, manager, lead и переключайте разрезы между лидами, менеджерами и desk-блоками без отдельной страницы.',
        tabs: {
            leads: 'Leads',
            managers: 'Managers',
            desks: 'Desks',
        },
        cards: {
            accessibleLeads: 'Лиды в срезе',
            netDeposit: 'Net deposit',
            paymentsCount: 'Платежные события',
            activityTotal: 'Общая активность',
            groupsInScope: 'Объектов в срезе',
            comments: 'Комментарии',
            statusChanges: 'Смены статусов',
            ordersOpened: 'Открыто ордеров',
        },
        stats: {
            deposits: 'Депозиты',
            withdrawals: 'Выводы',
            bonuses: 'Бонусы',
            newLeads: 'Новые лиды',
            ordersOpened: 'Открыто',
            ordersClosed: 'Закрыто',
            comments: 'Комментарии',
            logs: 'Логи',
            statusChanges: 'Смены статусов',
            leads: 'Лиды',
            net: 'Net',
        },
        charts: {
            payments: 'Cashflow by day',
            orders: 'Orders flow',
            saleStatuses: 'Sale statuses',
            retentionStatuses: 'Retention statuses',
            moneyCompare: 'Money comparison',
            activityCompare: 'Activity comparison',
            depositShare: 'Deposit share',
        },
        tables: {
            leads: 'Lead analytics',
            managers: 'Manager analytics',
            desks: 'Desk analytics',
            leadsHint: 'Топ-15 лидов по депозитной и операционной активности',
            managersHint: 'Сравнение менеджеров по лидам, деньгам и активности за выбранный период',
            desksHint: 'Сравнение desk-блоков по лидам, деньгам и активности за выбранный период',
        },
        columns: {
            name: 'Name',
            leads: 'Leads',
            deposits: 'Deposits',
            withdrawals: 'Withdrawals',
            bonus: 'Bonus',
            net: 'Net',
            opened: 'Opened',
            closed: 'Closed',
            activity: 'Activity',
            comments: 'Comments',
            logs: 'Logs',
            statusChanges: 'Status changes',
            desk: 'Desk',
            manager: 'Manager',
            saleStatus: 'Sale status',
            retentionStatus: 'Retention status',
        },
    },
    en: {
        title: 'Analytics',
        accessDenied: 'Analytics access denied',
        loadError: 'Failed to load analytics.',
        apply: 'Apply',
        reset: 'Reset',
        all: 'All',
        lead: 'Lead',
        manager: 'Manager',
        desk: 'Desk',
        from: 'From date',
        to: 'To date',
        quickRanges: {
            sevenDays: 'Last 7 days',
            oneMonth: 'Last month',
            threeMonths: 'Last 3 months',
        },
        empty: 'Not enough data for the selected period.',
        currentSlice: 'Current slice',
        dateRange: 'Date range',
        scope: 'Filters',
        statusBreakdown: 'Statuses',
        filtersHint: 'Filter analytics by desk, manager, or lead and switch between lead, manager, and desk views without leaving the page.',
        tabs: {
            leads: 'Leads',
            managers: 'Managers',
            desks: 'Desks',
        },
        cards: {
            accessibleLeads: 'Leads in scope',
            netDeposit: 'Net deposit',
            paymentsCount: 'Payment events',
            activityTotal: 'Total activity',
            groupsInScope: 'Entities in scope',
            comments: 'Comments',
            statusChanges: 'Status changes',
            ordersOpened: 'Orders opened',
        },
        stats: {
            deposits: 'Deposits',
            withdrawals: 'Withdrawals',
            bonuses: 'Bonuses',
            newLeads: 'New leads',
            ordersOpened: 'Opened',
            ordersClosed: 'Closed',
            comments: 'Comments',
            logs: 'Logs',
            statusChanges: 'Status changes',
            leads: 'Leads',
            net: 'Net',
        },
        charts: {
            payments: 'Cashflow by day',
            orders: 'Orders flow',
            saleStatuses: 'Sale statuses',
            retentionStatuses: 'Retention statuses',
            moneyCompare: 'Money comparison',
            activityCompare: 'Activity comparison',
            depositShare: 'Deposit share',
        },
        tables: {
            leads: 'Lead analytics',
            managers: 'Manager analytics',
            desks: 'Desk analytics',
            leadsHint: 'Top-15 leads by deposit and operational activity',
            managersHint: 'Compare managers by lead base, money, and activity for the selected period',
            desksHint: 'Compare desks by lead base, money, and activity for the selected period',
        },
        columns: {
            name: 'Name',
            leads: 'Leads',
            deposits: 'Deposits',
            withdrawals: 'Withdrawals',
            bonus: 'Bonus',
            net: 'Net',
            opened: 'Opened',
            closed: 'Closed',
            activity: 'Activity',
            comments: 'Comments',
            logs: 'Logs',
            statusChanges: 'Status changes',
            desk: 'Desk',
            manager: 'Manager',
            saleStatus: 'Sale status',
            retentionStatus: 'Retention status',
        },
    },
};

const permissionNames = {
    listLeads: 'lead.list_leads',
};

const ENTITY_COLORS = ['#0f766e', '#14b8a6', '#22c55e', '#3b82f6', '#f59e0b', '#ef4444'];

const normalizeLeadStatus = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');

const STATUS_BADGE_COLOR_MAP = {
    new: ['#35d767', '#1fb855'],
    callback: ['#35d767', '#1fb855'],
    askedtocalllater: ['#35d767', '#1fb855'],
    deposit: ['#35d767', '#1fb855'],
    wronginfo: ['#ff4e4e', '#e32626'],
    wrongnumber: ['#ff4e4e', '#e32626'],
    spam: ['#ff4e4e', '#e32626'],
    wronglanguage: ['#ff4e4e', '#e32626'],
    under18: ['#ff4e4e', '#e32626'],
    underage: ['#ff4e4e', '#e32626'],
    overage: ['#ff4e4e', '#e32626'],
    notinportrait: ['#ff4e4e', '#e32626'],
    undercriteria: ['#ff4e4e', '#e32626'],
    wronggeo: ['#ff4e4e', '#e32626'],
    ukraine: ['#ff4e4e', '#e32626'],
    didntregister: ['#ff4e4e', '#e32626'],
    calledbyothercompany: ['#ff4e4e', '#e32626'],
    trash: ['#ff4e4e', '#e32626'],
    noanswer: ['#4e5cf7', '#323fcf'],
    noanswerni: ['#4e5cf7', '#323fcf'],
    neveranswer: ['#4e5cf7', '#323fcf'],
    voicemail: ['#4e5cf7', '#323fcf'],
    notinterested: ['#9f6a43', '#7b4b27'],
    test: ['#ff5dc2', '#e93ba3'],
    nomoney: ['#9f6a43', '#7b4b27'],
    partialdeposit: ['#35d767', '#1fb855'],
};

const getLeadStatusChartColors = (statusValue) => {
    const normalizedStatus = normalizeLeadStatus(statusValue);

    if (!normalizedStatus) {
        return ['#94a3b8', '#64748b'];
    }

    if (STATUS_BADGE_COLOR_MAP[normalizedStatus]) {
        return STATUS_BADGE_COLOR_MAP[normalizedStatus];
    }

    if (normalizedStatus.includes('deposit') || normalizedStatus.includes('callback') || normalizedStatus.includes('calllater')) {
        return ['#35d767', '#1fb855'];
    }

    if (
        normalizedStatus.includes('wrong')
        || normalizedStatus.includes('spam')
        || normalizedStatus.includes('trash')
        || normalizedStatus.includes('under')
        || normalizedStatus.includes('over')
        || normalizedStatus.includes('criteria')
        || normalizedStatus.includes('geo')
        || normalizedStatus.includes('register')
        || normalizedStatus.includes('othercompany')
    ) {
        return ['#ff4e4e', '#e32626'];
    }

    if (
        normalizedStatus.includes('noanswer')
        || normalizedStatus.includes('neveranswer')
        || normalizedStatus.includes('voicemail')
    ) {
        return ['#4e5cf7', '#323fcf'];
    }

    if (normalizedStatus.includes('test')) {
        return ['#ff5dc2', '#e93ba3'];
    }

    if (normalizedStatus.includes('notinterested') || normalizedStatus.includes('money')) {
        return ['#9f6a43', '#7b4b27'];
    }

    return ['#4e5cf7', '#323fcf'];
};

const createDefaultFilters = () => {
    const today = new Date();
    const from = new Date(today);
    from.setDate(today.getDate() - 29);

    return {
        fromDate: from.toISOString().slice(0, 10),
        toDate: today.toISOString().slice(0, 10),
        leadId: '',
        managerId: '',
        deskId: '',
        managerIds: [],
        deskIds: [],
    };
};

const formatMoney = (value) =>
    new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(Number(value || 0));

const formatInteger = (value) => new Intl.NumberFormat('en-US').format(Number(value || 0));

const formatShortDate = (value) => {
    if (!value) {
        return '';
    }

    const [year, month, day] = value.split('-');
    return `${day}.${month}`;
};

const toSelectorOptions = (items = [], allLabel) => [
    { value: '', label: allLabel },
    ...items.map((item) => ({
        value: item.value,
        label: item.label,
    })),
];

const truncateLabel = (value, maxLength = 14) => {
    const label = String(value || '');
    return label.length > maxLength ? `${label.slice(0, maxLength - 1)}…` : label;
};

const formatInputDate = (value) => value.toISOString().slice(0, 10);

const createPresetRange = (presetKey) => {
    const to = new Date();
    to.setHours(12, 0, 0, 0);
    const from = new Date(to);

    if (presetKey === 'sevenDays') {
        from.setDate(to.getDate() - 6);
    }

    if (presetKey === 'oneMonth') {
        from.setMonth(to.getMonth() - 1);
        from.setDate(from.getDate() + 1);
    }

    if (presetKey === 'threeMonths') {
        from.setMonth(to.getMonth() - 3);
        from.setDate(from.getDate() + 1);
    }

    return {
        fromDate: formatInputDate(from),
        toDate: formatInputDate(to),
    };
};

const buildSmoothPath = (points, width, height, maxValue) => {
    if (!points.length) {
        return '';
    }

    return points
        .map((value, index) => {
            const x = points.length === 1 ? width / 2 : (index / (points.length - 1)) * width;
            const y = height - (maxValue === 0 ? 0 : (value / maxValue) * height);

            return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
        })
        .join(' ');
};

const buildAreaPath = (points, width, height, maxValue) => {
    if (!points.length) {
        return '';
    }

    const linePath = buildSmoothPath(points, width, height, maxValue);
    const lastX = points.length === 1 ? width / 2 : width;

    return `${linePath} L ${lastX} ${height} L 0 ${height} Z`;
};

const aggregateRows = (rows = []) => rows.reduce((acc, row) => ({
    id: 'aggregate',
    name: 'Aggregate',
    leads_total: acc.leads_total + Number(row.leads_total || 0),
    deposits_total: acc.deposits_total + Number(row.deposits_total || 0),
    withdrawals_total: acc.withdrawals_total + Number(row.withdrawals_total || 0),
    bonus_total: acc.bonus_total + Number(row.bonus_total || 0),
    net_deposit: acc.net_deposit + Number(row.net_deposit || 0),
    orders_opened: acc.orders_opened + Number(row.orders_opened || 0),
    orders_closed: acc.orders_closed + Number(row.orders_closed || 0),
    comments_count: acc.comments_count + Number(row.comments_count || 0),
    logs_count: acc.logs_count + Number(row.logs_count || 0),
    status_changes_count: acc.status_changes_count + Number(row.status_changes_count || 0),
    activity_total: acc.activity_total + Number(row.activity_total || 0),
}), {
    id: 'aggregate',
    name: 'Aggregate',
    leads_total: 0,
    deposits_total: 0,
    withdrawals_total: 0,
    bonus_total: 0,
    net_deposit: 0,
    orders_opened: 0,
    orders_closed: 0,
    comments_count: 0,
    logs_count: 0,
    status_changes_count: 0,
    activity_total: 0,
});

const SummaryCard = ({ title, value, icon, tone, detail, theme }) => (
    <div className={`${styles.summaryCard} ${theme === 'dark' ? styles.darkCard : ''}`}>
        <div className={`${styles.summaryIcon} ${styles[tone]}`}>
            {icon}
        </div>
        <div className={styles.summaryMeta}>
            <span className={styles.summaryTitle}>{title}</span>
            <strong className={styles.summaryValue}>{value}</strong>
            {detail ? <span className={styles.summaryDetail}>{detail}</span> : null}
        </div>
    </div>
);

const StatChip = ({ label, value, tone }) => (
    <div className={`${styles.statChip} ${styles[tone]}`}>
        <span>{label}</span>
        <strong>{value}</strong>
    </div>
);

const ChartShell = ({ title, subtitle, children, theme }) => (
    <section className={`${styles.chartCard} ${theme === 'dark' ? styles.darkCard : ''}`}>
        <div className={styles.cardHeader}>
            <div>
                <h3>{title}</h3>
                {subtitle ? <p className={styles.cardHint}>{subtitle}</p> : null}
            </div>
        </div>
        {children}
    </section>
);

const MixedBarLineChart = ({ data, theme, emptyLabel, barKeys, lineKey, valueFormatter = formatMoney }) => {
    const width = 760;
    const height = 260;
    const padding = { top: 16, right: 10, bottom: 40, left: 12 };
    const innerHeight = height - padding.top - padding.bottom;
    const barGroupWidth = data.length === 0 ? 0 : width / data.length;
    const [tooltip, setTooltip] = useState(null);
    const maxValue = Math.max(
        1,
        ...data.flatMap((item) => [...barKeys.map((key) => Number(item[key.key] || 0)), Number(item[lineKey.key] || 0)])
    );

    if (data.length === 0) {
        return <div className={styles.emptyState}>{emptyLabel}</div>;
    }

    const lineValues = data.map((item) => Number(item[lineKey.key] || 0));

    return (
        <div className={styles.chartBody}>
            <div className={styles.legendRow}>
                {barKeys.map((item) => (
                    <span key={item.key} className={styles.legendItem}>
                        <span className={styles.legendDot} style={{ background: item.color }} />
                        {item.label}
                    </span>
                ))}
                <span className={styles.legendItem}>
                    <span className={styles.legendLine} style={{ background: lineKey.color }} />
                    {lineKey.label}
                </span>
            </div>

            <div className={styles.interactiveChartWrap}>
                <svg className={styles.chartSvg} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={lineKey.label}>
                    {[0, 0.25, 0.5, 0.75, 1].map((tick) => {
                        const y = padding.top + innerHeight - innerHeight * tick;
                        return <line key={tick} x1="0" x2={width} y1={y} y2={y} className={styles.chartGridLine} />;
                    })}

                    {data.map((item, index) => {
                        const groupX = index * barGroupWidth;
                        const barWidth = Math.max(12, barGroupWidth / (barKeys.length + 1.6));

                        return (
                            <g key={item.date}>
                                {barKeys.map((bar, barIndex) => {
                                    const value = Number(item[bar.key] || 0);
                                    const barHeight = (value / maxValue) * innerHeight;
                                    const x = groupX + 10 + barIndex * (barWidth + 8);
                                    const y = padding.top + innerHeight - barHeight;

                                    return (
                                        <rect
                                            key={bar.key}
                                            x={x}
                                            y={y}
                                            width={barWidth}
                                            height={Math.max(barHeight, 2)}
                                            rx="10"
                                            fill={bar.color}
                                            onMouseEnter={(event) => {
                                                const bounds = event.currentTarget.ownerSVGElement?.getBoundingClientRect();
                                                if (!bounds) {
                                                    return;
                                                }

                                                setTooltip({
                                                    x: event.clientX - bounds.left + 12,
                                                    y: event.clientY - bounds.top - 12,
                                                    label: bar.label,
                                                    value: valueFormatter(value),
                                                    date: formatShortDate(item.date),
                                                    color: bar.color,
                                                });
                                            }}
                                            onMouseMove={(event) => {
                                                const bounds = event.currentTarget.ownerSVGElement?.getBoundingClientRect();
                                                if (!bounds) {
                                                    return;
                                                }

                                                setTooltip((prev) => prev ? {
                                                    ...prev,
                                                    x: event.clientX - bounds.left + 12,
                                                    y: event.clientY - bounds.top - 12,
                                                } : prev);
                                            }}
                                            onMouseLeave={() => setTooltip(null)}
                                        />
                                    );
                                })}

                                <text
                                    x={groupX + barGroupWidth / 2}
                                    y={height - 10}
                                    textAnchor="middle"
                                    className={styles.chartAxisLabel}
                                >
                                    {formatShortDate(item.date)}
                                </text>
                            </g>
                        );
                    })}

                    <path
                        d={buildSmoothPath(lineValues, width, innerHeight, maxValue)
                            .replaceAll(/L ([\d.]+) ([\d.]+)/g, (_, x, y) => `L ${x} ${Number(y) + padding.top}`)
                            .replace(/^M ([\d.]+) ([\d.]+)/, (_, x, y) => `M ${x} ${Number(y) + padding.top}`)}
                        fill="none"
                        stroke={lineKey.color}
                        strokeWidth="4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    {data.map((item, index) => {
                        const value = Number(item[lineKey.key] || 0);
                        const x = data.length === 1 ? width / 2 : (index / (data.length - 1)) * width;
                        const y = padding.top + innerHeight - (value / maxValue) * innerHeight;

                        return <circle key={`${item.date}-${lineKey.key}`} cx={x} cy={y} r="5" fill={lineKey.color} />;
                    })}
                </svg>
                {tooltip ? (
                    <div
                        className={`${styles.chartTooltip} ${theme === 'dark' ? styles.darkChartTooltip : ''}`}
                        style={{ left: tooltip.x, top: tooltip.y }}
                    >
                        <span className={styles.chartTooltipDate}>{tooltip.date}</span>
                        <span className={styles.chartTooltipMetric}>
                            <span className={styles.legendDot} style={{ background: tooltip.color }} />
                            {tooltip.label}: <strong>{tooltip.value}</strong>
                        </span>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

const AreaBarsChart = ({ data, emptyLabel, areaKey, barKey, areaLabel, barLabel, gradientId }) => {
    const width = 760;
    const height = 250;
    const paddingTop = 18;
    const chartHeight = 180;
    const maxValue = Math.max(1, ...data.flatMap((item) => [Number(item[areaKey] || 0), Number(item[barKey] || 0)]));

    if (data.length === 0) {
        return <div className={styles.emptyState}>{emptyLabel}</div>;
    }

    const areaValues = data.map((item) => Number(item[areaKey] || 0));

    return (
        <div className={styles.chartBody}>
            <div className={styles.legendRow}>
                <span className={styles.legendItem}>
                    <span className={styles.legendDot} style={{ background: '#244669' }} />
                    {barLabel}
                </span>
                <span className={styles.legendItem}>
                    <span className={styles.legendDot} style={{ background: '#7dd3fc' }} />
                    {areaLabel}
                </span>
            </div>

            <svg className={styles.chartSvg} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={areaLabel}>
                {[0, 0.25, 0.5, 0.75, 1].map((tick) => {
                    const y = paddingTop + chartHeight - chartHeight * tick;
                    return <line key={tick} x1="0" x2={width} y1={y} y2={y} className={styles.chartGridLine} />;
                })}

                <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#7dd3fc" stopOpacity="0.84" />
                        <stop offset="100%" stopColor="#7dd3fc" stopOpacity="0.10" />
                    </linearGradient>
                </defs>

                <path
                    d={buildAreaPath(areaValues, width, chartHeight, maxValue)
                        .replaceAll(/L ([\d.]+) ([\d.]+)/g, (_, x, y) => `L ${x} ${Number(y) + paddingTop}`)
                        .replace(/^M ([\d.]+) ([\d.]+)/, (_, x, y) => `M ${x} ${Number(y) + paddingTop}`)}
                    fill={`url(#${gradientId})`}
                    opacity="0.92"
                />

                {data.map((item, index) => {
                    const value = Number(item[barKey] || 0);
                    const barHeight = (value / maxValue) * chartHeight;
                    const barWidth = Math.max(16, width / (data.length * 2.8));
                    const x = data.length === 1 ? width / 2 - barWidth / 2 : (index / data.length) * width + 18;
                    const y = paddingTop + chartHeight - barHeight;

                    return (
                        <g key={item.date}>
                            <rect x={x} y={y} width={barWidth} height={Math.max(barHeight, 2)} rx="10" fill="#244669" opacity="0.84" />
                            <text x={x + barWidth / 2} y={height - 10} textAnchor="middle" className={styles.chartAxisLabel}>
                                {formatShortDate(item.date)}
                            </text>
                        </g>
                    );
                })}
            </svg>
        </div>
    );
};

const DonutChart = ({ data, emptyLabel, totalLabel, gradientPrefix }) => {
    if (!data.length) {
        return <div className={styles.emptyState}>{emptyLabel}</div>;
    }

    const radius = 58;
    const stroke = 22;
    const circumference = 2 * Math.PI * radius;
    const total = data.reduce((acc, item) => acc + Number(item.leads_total || 0), 0);

    let offset = 0;

    return (
        <div className={styles.donutLayout}>
            <div className={styles.donutWrap}>
                <svg width="180" height="180" viewBox="0 0 180 180">
                    <circle cx="90" cy="90" r={radius} fill="none" stroke="#e7eef6" strokeWidth={stroke} />
                    {data.slice(0, 6).map((item, index) => {
                        const value = Number(item.leads_total || 0);
                        const segment = total === 0 ? 0 : (value / total) * circumference;
                        const [startColor, endColor] = getLeadStatusChartColors(item.status);
                        const gradientId = `${gradientPrefix}-${index}`;
                        const circle = (
                            <g key={item.status}>
                                <defs>
                                    <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
                                        <stop offset="0%" stopColor={startColor} />
                                        <stop offset="100%" stopColor={endColor} />
                                    </linearGradient>
                                </defs>
                                <circle
                                    cx="90"
                                    cy="90"
                                    r={radius}
                                    fill="none"
                                    stroke={`url(#${gradientId})`}
                                    strokeWidth={stroke}
                                    strokeLinecap="round"
                                    strokeDasharray={`${segment} ${circumference - segment}`}
                                    strokeDashoffset={-offset}
                                    transform="rotate(-90 90 90)"
                                />
                            </g>
                        );
                        offset += segment;
                        return circle;
                    })}
                </svg>
                <div className={styles.donutCenter}>
                    <strong>{formatInteger(total)}</strong>
                    <span>{totalLabel}</span>
                </div>
            </div>

            <div className={styles.donutLegend}>
                {data.slice(0, 6).map((item) => {
                    const [startColor] = getLeadStatusChartColors(item.status);

                    return (
                        <div key={item.status} className={styles.donutLegendItem}>
                            <span className={styles.legendDot} style={{ background: startColor }} />
                            <span className={styles.donutLegendLabel}>{item.status}</span>
                            <strong>{formatInteger(item.leads_total)}</strong>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const TimelineCompareChart = ({ timeline, theme, emptyLabel, valueFormatter = formatInteger }) => {
    const width = 760;
    const height = 260;
    const padding = { top: 18, right: 18, bottom: 42, left: 12 };
    const innerHeight = height - padding.top - padding.bottom;
    const [tooltip, setTooltip] = useState(null);
    const data = Array.isArray(timeline) ? timeline : [];
    const entities = data[0]?.series ?? [];
    const maxValue = Math.max(
        1,
        ...data.flatMap((point) => (point.series ?? []).map((seriesItem) => Number(seriesItem.value || 0)))
    );

    if (data.length === 0 || entities.length === 0) {
        return <div className={styles.emptyState}>{emptyLabel}</div>;
    }

    const getX = (index) => (data.length === 1 ? width / 2 : (index / (data.length - 1)) * width);
    const getY = (value) => padding.top + innerHeight - ((Number(value || 0) / maxValue) * innerHeight);

    return (
        <div className={styles.chartBody}>
            <div className={styles.legendRow}>
                {entities.map((entity, index) => (
                    <span key={entity.key} className={styles.legendItem}>
                        <span className={styles.legendDot} style={{ background: ENTITY_COLORS[index % ENTITY_COLORS.length] }} />
                        {entity.name}
                    </span>
                ))}
            </div>

            <div className={styles.interactiveChartWrap}>
                <svg className={styles.chartSvg} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="timeline-comparison">
                    {[0, 0.25, 0.5, 0.75, 1].map((tick) => {
                        const y = padding.top + innerHeight - innerHeight * tick;
                        return <line key={tick} x1="0" x2={width} y1={y} y2={y} className={styles.chartGridLine} />;
                    })}

                    {entities.map((entity, entityIndex) => {
                        const color = ENTITY_COLORS[entityIndex % ENTITY_COLORS.length];
                        const values = data.map((point) => {
                            const match = (point.series ?? []).find((seriesItem) => seriesItem.key === entity.key);
                            return Number(match?.value || 0);
                        });

                        return (
                            <g key={entity.key}>
                                <path
                                    d={buildSmoothPath(values, width, innerHeight, maxValue)
                                        .replaceAll(/L ([\d.]+) ([\d.]+)/g, (_, x, y) => `L ${x} ${Number(y) + padding.top}`)
                                        .replace(/^M ([\d.]+) ([\d.]+)/, (_, x, y) => `M ${x} ${Number(y) + padding.top}`)}
                                    fill="none"
                                    stroke={color}
                                    strokeWidth="3.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                {data.map((point, pointIndex) => {
                                    const match = (point.series ?? []).find((seriesItem) => seriesItem.key === entity.key);
                                    const value = Number(match?.value || 0);
                                    const x = getX(pointIndex);
                                    const y = getY(value);

                                    return (
                                        <circle
                                            key={`${entity.key}-${point.date}`}
                                            cx={x}
                                            cy={y}
                                            r="6"
                                            fill={color}
                                            onMouseEnter={(event) => {
                                                const bounds = event.currentTarget.ownerSVGElement?.getBoundingClientRect();
                                                if (!bounds) {
                                                    return;
                                                }

                                                setTooltip({
                                                    x: event.clientX - bounds.left + 12,
                                                    y: event.clientY - bounds.top - 12,
                                                    label: entity.name,
                                                    value: valueFormatter(value),
                                                    date: formatShortDate(point.date),
                                                    color,
                                                });
                                            }}
                                            onMouseMove={(event) => {
                                                const bounds = event.currentTarget.ownerSVGElement?.getBoundingClientRect();
                                                if (!bounds) {
                                                    return;
                                                }

                                                setTooltip((prev) => prev ? {
                                                    ...prev,
                                                    x: event.clientX - bounds.left + 12,
                                                    y: event.clientY - bounds.top - 12,
                                                } : prev);
                                            }}
                                            onMouseLeave={() => setTooltip(null)}
                                        />
                                    );
                                })}
                            </g>
                        );
                    })}

                    {data.map((point, index) => (
                        <text
                            key={point.date}
                            x={getX(index)}
                            y={height - 10}
                            textAnchor="middle"
                            className={styles.chartAxisLabel}
                        >
                            {formatShortDate(point.date)}
                        </text>
                    ))}
                </svg>

                {tooltip ? (
                    <div
                        className={`${styles.chartTooltip} ${theme === 'dark' ? styles.darkChartTooltip : ''}`}
                        style={{ left: tooltip.x, top: tooltip.y }}
                    >
                        <span className={styles.chartTooltipDate}>{tooltip.date}</span>
                        <span className={styles.chartTooltipMetric}>
                            <span className={styles.legendDot} style={{ background: tooltip.color }} />
                            {tooltip.label}: <strong>{tooltip.value}</strong>
                        </span>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

const GroupedBarsChart = ({ rows, emptyLabel, series, valueFormatter = formatMoney, theme, chartId }) => {
    const [tooltip, setTooltip] = useState(null);
    const data = rows.slice(0, 6);
    const width = 760;
    const height = 280;
    const padding = { top: 18, right: 18, bottom: 54, left: 12 };
    const innerHeight = height - padding.top - padding.bottom;
    const groupWidth = data.length === 0 ? 0 : width / data.length;
    const maxValue = Math.max(1, ...data.flatMap((row) => series.map((item) => Number(row[item.key] || 0))));

    if (!data.length) {
        return <div className={styles.emptyState}>{emptyLabel}</div>;
    }

    return (
        <div className={styles.chartBody}>
            <div className={styles.legendRow}>
                {series.map((item) => (
                    <span key={item.key} className={styles.legendItem}>
                        <span className={styles.legendDot} style={{ background: item.color }} />
                        {item.label}
                    </span>
                ))}
            </div>
            <div className={styles.interactiveChartWrap}>
                <svg className={styles.chartSvg} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={chartId}>
                    {[0, 0.25, 0.5, 0.75, 1].map((tick) => {
                        const y = padding.top + innerHeight - innerHeight * tick;
                        return <line key={tick} x1="0" x2={width} y1={y} y2={y} className={styles.chartGridLine} />;
                    })}
                    {data.map((row, rowIndex) => {
                        const barWidth = Math.max(18, groupWidth / (series.length + 1.8));
                        const offsetX = rowIndex * groupWidth + (groupWidth - (series.length * barWidth + (series.length - 1) * 8)) / 2;

                        return (
                            <g key={row.id ?? row.name}>
                                {series.map((item, seriesIndex) => {
                                    const value = Number(row[item.key] || 0);
                                    const barHeight = (value / maxValue) * innerHeight;
                                    const x = offsetX + seriesIndex * (barWidth + 8);
                                    const y = padding.top + innerHeight - barHeight;

                                    return (
                                        <rect
                                            key={item.key}
                                            x={x}
                                            y={y}
                                            width={barWidth}
                                            height={Math.max(barHeight, 2)}
                                            rx="10"
                                            fill={item.color}
                                            onMouseEnter={(event) => {
                                                const bounds = event.currentTarget.ownerSVGElement?.getBoundingClientRect();
                                                if (!bounds) {
                                                    return;
                                                }

                                                setTooltip({
                                                    x: event.clientX - bounds.left + 12,
                                                    y: event.clientY - bounds.top - 12,
                                                    name: row.name,
                                                    label: item.label,
                                                    value: valueFormatter(value),
                                                    color: item.color,
                                                });
                                            }}
                                            onMouseMove={(event) => {
                                                const bounds = event.currentTarget.ownerSVGElement?.getBoundingClientRect();
                                                if (!bounds) {
                                                    return;
                                                }

                                                setTooltip((prev) => prev ? {
                                                    ...prev,
                                                    x: event.clientX - bounds.left + 12,
                                                    y: event.clientY - bounds.top - 12,
                                                } : prev);
                                            }}
                                            onMouseLeave={() => setTooltip(null)}
                                        />
                                    );
                                })}
                                <text x={rowIndex * groupWidth + groupWidth / 2} y={height - 16} textAnchor="middle" className={styles.chartAxisLabel}>
                                    {truncateLabel(row.name)}
                                </text>
                            </g>
                        );
                    })}
                </svg>
                {tooltip ? (
                    <div
                        className={`${styles.chartTooltip} ${theme === 'dark' ? styles.darkChartTooltip : ''}`}
                        style={{ left: tooltip.x, top: tooltip.y }}
                    >
                        <span className={styles.chartTooltipDate}>{tooltip.name}</span>
                        <span className={styles.chartTooltipMetric}>
                            <span className={styles.legendDot} style={{ background: tooltip.color }} />
                            {tooltip.label}: <strong>{tooltip.value}</strong>
                        </span>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

const StackedComparisonChart = ({ rows, emptyLabel, metrics }) => {
    const data = rows.slice(0, 6);
    const width = 760;
    const height = 280;
    const padding = { top: 18, right: 24, bottom: 54, left: 16 };
    const innerHeight = height - padding.top - padding.bottom;
    const groupWidth = data.length === 0 ? 0 : width / data.length;
    const maxValue = Math.max(1, ...data.map((row) => metrics.reduce((sum, item) => sum + Number(row[item.key] || 0), 0)));

    if (!data.length) {
        return <div className={styles.emptyState}>{emptyLabel}</div>;
    }

    return (
        <div className={styles.chartBody}>
            <div className={styles.legendRow}>
                {metrics.map((item) => (
                    <span key={item.key} className={styles.legendItem}>
                        <span className={styles.legendDot} style={{ background: item.color }} />
                        {item.label}
                    </span>
                ))}
            </div>
            <svg className={styles.chartSvg} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="stacked-comparison">
                {[0, 0.25, 0.5, 0.75, 1].map((tick) => {
                    const y = padding.top + innerHeight - innerHeight * tick;
                    return <line key={tick} x1="0" x2={width} y1={y} y2={y} className={styles.chartGridLine} />;
                })}
                {data.map((row, rowIndex) => {
                    const barWidth = Math.max(34, groupWidth * 0.46);
                    const x = rowIndex * groupWidth + (groupWidth - barWidth) / 2;
                    let stackOffset = 0;

                    return (
                        <g key={row.id ?? row.name}>
                            {metrics.map((item) => {
                                const value = Number(row[item.key] || 0);
                                const segmentHeight = (value / maxValue) * innerHeight;
                                const y = padding.top + innerHeight - stackOffset - segmentHeight;
                                stackOffset += segmentHeight;

                                return (
                                    <rect
                                        key={item.key}
                                        x={x}
                                        y={y}
                                        width={barWidth}
                                        height={Math.max(segmentHeight, value > 0 ? 6 : 0)}
                                        rx="10"
                                        fill={item.color}
                                        opacity="0.94"
                                    />
                                );
                            })}
                            <text x={rowIndex * groupWidth + groupWidth / 2} y={height - 16} textAnchor="middle" className={styles.chartAxisLabel}>
                                {truncateLabel(row.name)}
                            </text>
                        </g>
                    );
                })}
            </svg>
        </div>
    );
};

const ShareDonutChart = ({ rows, emptyLabel, labelKey, valueKey, totalLabel }) => {
    const data = rows
        .filter((row) => Number(row[valueKey] || 0) > 0)
        .slice(0, 6)
        .map((row, index) => ({
            label: row[labelKey],
            value: Number(row[valueKey] || 0),
            color: ENTITY_COLORS[index % ENTITY_COLORS.length],
        }));

    if (!data.length) {
        return <div className={styles.emptyState}>{emptyLabel}</div>;
    }

    const radius = 58;
    const stroke = 22;
    const circumference = 2 * Math.PI * radius;
    const total = data.reduce((acc, item) => acc + item.value, 0);
    let offset = 0;

    return (
        <div className={styles.donutLayout}>
            <div className={styles.donutWrap}>
                <svg width="180" height="180" viewBox="0 0 180 180">
                    <circle cx="90" cy="90" r={radius} fill="none" stroke="#e7eef6" strokeWidth={stroke} />
                    {data.map((item) => {
                        const segment = total === 0 ? 0 : (item.value / total) * circumference;
                        const circle = (
                            <circle
                                key={item.label}
                                cx="90"
                                cy="90"
                                r={radius}
                                fill="none"
                                stroke={item.color}
                                strokeWidth={stroke}
                                strokeLinecap="round"
                                strokeDasharray={`${segment} ${circumference - segment}`}
                                strokeDashoffset={-offset}
                                transform="rotate(-90 90 90)"
                            />
                        );
                        offset += segment;
                        return circle;
                    })}
                </svg>
                <div className={styles.donutCenter}>
                    <strong>{formatMoney(total)}</strong>
                    <span>{totalLabel}</span>
                </div>
            </div>
            <div className={styles.donutLegend}>
                {data.map((item) => (
                    <div key={item.label} className={styles.donutLegendItem}>
                        <span className={styles.legendDot} style={{ background: item.color }} />
                        <span className={styles.donutLegendLabel}>{item.label}</span>
                        <strong>{formatMoney(item.value)}</strong>
                    </div>
                ))}
            </div>
        </div>
    );
};

const SpotlightList = ({ rows, emptyLabel, metrics }) => {
    const data = rows.slice(0, 5);
    const maxValue = Math.max(1, ...data.flatMap((row) => metrics.map((item) => Number(row[item.key] || 0))));

    if (!data.length) {
        return <div className={styles.emptyState}>{emptyLabel}</div>;
    }

    return (
        <div className={styles.spotlightList}>
            {data.map((row) => (
                <div key={row.id ?? row.name} className={styles.spotlightRow}>
                    <div className={styles.spotlightHeader}>
                        <strong>{row.name}</strong>
                        <span>{formatInteger(row.leads_total)} {metrics[0].leadsLabel}</span>
                    </div>
                    <div className={styles.spotlightMetrics}>
                        {metrics.map((item) => {
                            const value = Number(row[item.key] || 0);
                            const width = `${Math.max((value / maxValue) * 100, value > 0 ? 8 : 0)}%`;

                            return (
                                <div key={item.key} className={styles.metricTrackRow}>
                                    <span className={styles.metricTrackLabel}>{item.label}</span>
                                    <div className={styles.metricTrack}>
                                        <span className={styles.metricTrackFill} style={{ width, background: item.color }} />
                                    </div>
                                    <strong>{item.formatter(value)}</strong>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
};

const DataTable = ({ title, hint, rows, columns, theme, emptyLabel }) => (
    <section className={`${styles.tableCard} ${theme === 'dark' ? styles.darkCard : ''}`}>
        <div className={styles.cardHeader}>
            <div>
                <h3>{title}</h3>
                {hint ? <p className={styles.cardHint}>{hint}</p> : null}
            </div>
        </div>

        {rows.length === 0 ? (
            <div className={styles.emptyState}>{emptyLabel}</div>
        ) : (
            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead className={styles.tableHeader}>
                        <tr>
                            {columns.map((column) => (
                                <th key={column.key}>{column.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => (
                            <tr key={row.id ?? `${row.name}-${index}`} className={styles.tableRow}>
                                {columns.map((column) => (
                                    <td key={column.key} className={styles.tableCell}>
                                        {column.render ? column.render(row[column.key], row) : row[column.key]}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
    </section>
);

const LeadsTab = ({ summary, charts, rankings, theme, t }) => {
    const summaryCards = [
        {
            title: t.cards.accessibleLeads,
            value: formatInteger(summary.accessible_leads),
            icon: <Groups2OutlinedIcon fontSize="small" />,
            tone: 'toneBlue',
            detail: `${t.stats.newLeads}: ${formatInteger(summary.new_leads)}`,
        },
        {
            title: t.cards.netDeposit,
            value: formatMoney(summary.net_deposit),
            icon: <PaidOutlinedIcon fontSize="small" />,
            tone: 'toneGreen',
            detail: `${t.stats.deposits}: ${formatMoney(summary.deposits_total)}`,
        },
        {
            title: t.cards.paymentsCount,
            value: formatInteger(summary.payments_count),
            icon: <ReceiptLongOutlinedIcon fontSize="small" />,
            tone: 'toneOrange',
            detail: `${t.stats.withdrawals}: ${formatMoney(summary.withdrawals_total)}`,
        },
        {
            title: t.cards.activityTotal,
            value: formatInteger(summary.activity_total),
            icon: <AnalyticsOutlinedIcon fontSize="small" />,
            tone: 'tonePurple',
            detail: `${t.stats.ordersOpened}: ${formatInteger(summary.orders_opened)} / ${t.stats.ordersClosed}: ${formatInteger(summary.orders_closed)}`,
        },
    ];

    const leadColumns = [
        { key: 'name', label: t.columns.name },
        { key: 'desk_name', label: t.columns.desk },
        { key: 'manager_name', label: t.columns.manager },
        { key: 'sale_status', label: t.columns.saleStatus },
        { key: 'retention_status', label: t.columns.retentionStatus },
        { key: 'deposits_total', label: t.columns.deposits, render: (value) => formatMoney(value) },
        { key: 'net_deposit', label: t.columns.net, render: (value) => formatMoney(value) },
        { key: 'comments_count', label: t.columns.comments, render: (value) => formatInteger(value) },
        { key: 'status_changes_count', label: t.columns.statusChanges, render: (value) => formatInteger(value) },
        { key: 'activity_total', label: t.columns.activity, render: (value) => formatInteger(value) },
    ];

    return (
        <>
            <div className={styles.summaryGrid}>
                {summaryCards.map((card) => (
                    <SummaryCard key={card.title} {...card} theme={theme} />
                ))}
            </div>

            <div className={styles.statsRow}>
                <StatChip label={t.stats.deposits} value={formatMoney(summary.deposits_total)} tone="chipGreen" />
                <StatChip label={t.stats.withdrawals} value={formatMoney(summary.withdrawals_total)} tone="chipRed" />
                <StatChip label={t.stats.bonuses} value={formatMoney(summary.bonus_total)} tone="chipYellow" />
                <StatChip label={t.stats.comments} value={formatInteger(summary.comments_count)} tone="chipBlue" />
                <StatChip label={t.stats.statusChanges} value={formatInteger(summary.status_changes_count)} tone="chipPurple" />
            </div>

            <div className={styles.chartGrid}>
                <ChartShell title={t.charts.payments} subtitle={t.dateRange} theme={theme}>
                    <MixedBarLineChart
                        data={charts.payments_by_day ?? []}
                        theme={theme}
                        emptyLabel={t.empty}
                        barKeys={[
                            { key: 'deposits_total', label: t.stats.deposits, color: '#22c55e' },
                            { key: 'withdrawals_total', label: t.stats.withdrawals, color: '#ef4444' },
                        ]}
                        lineKey={{ key: 'net_deposit', label: t.cards.netDeposit, color: '#0f3d56' }}
                    />
                </ChartShell>

                <ChartShell title={t.statusBreakdown} subtitle={t.charts.saleStatuses} theme={theme}>
                    <DonutChart
                        data={charts.sale_statuses ?? []}
                        emptyLabel={t.empty}
                        totalLabel={t.columns.leads}
                        gradientPrefix="sale-status"
                    />
                </ChartShell>

                <ChartShell title={t.charts.orders} subtitle={t.dateRange} theme={theme}>
                    <AreaBarsChart
                        data={charts.orders_by_day ?? []}
                        emptyLabel={t.empty}
                        areaKey="closed"
                        barKey="opened"
                        areaLabel={t.stats.ordersClosed}
                        barLabel={t.stats.ordersOpened}
                        gradientId="orders-flow-gradient"
                    />
                </ChartShell>

                <ChartShell title={t.charts.retentionStatuses} subtitle={t.scope} theme={theme}>
                    <DonutChart
                        data={charts.retention_statuses ?? []}
                        emptyLabel={t.empty}
                        totalLabel={t.columns.leads}
                        gradientPrefix="retention-status"
                    />
                </ChartShell>
            </div>

            <div className={styles.singleTableGrid}>
                <DataTable
                    title={t.tables.leads}
                    hint={t.tables.leadsHint}
                    rows={rankings.leads ?? []}
                    columns={leadColumns}
                    theme={theme}
                    emptyLabel={t.empty}
                />
            </div>
        </>
    );
};

const GroupTab = ({ rows, groupCharts, theme, t, tableTitle, tableHint, type }) => {
    const aggregate = aggregateRows(rows);
    const summaryCards = [
        {
            title: t.cards.groupsInScope,
            value: formatInteger(rows.length),
            icon: <Groups2OutlinedIcon fontSize="small" />,
            tone: 'toneBlue',
            detail: `${t.stats.leads}: ${formatInteger(aggregate.leads_total)}`,
        },
        {
            title: t.cards.netDeposit,
            value: formatMoney(aggregate.net_deposit),
            icon: <PaidOutlinedIcon fontSize="small" />,
            tone: 'toneGreen',
            detail: `${t.stats.deposits}: ${formatMoney(aggregate.deposits_total)}`,
        },
        {
            title: t.cards.comments,
            value: formatInteger(aggregate.comments_count),
            icon: <ForumOutlinedIcon fontSize="small" />,
            tone: 'toneOrange',
            detail: `${t.stats.logs}: ${formatInteger(aggregate.logs_count)}`,
        },
        {
            title: t.cards.statusChanges,
            value: formatInteger(aggregate.status_changes_count),
            icon: <AutorenewOutlinedIcon fontSize="small" />,
            tone: 'tonePurple',
            detail: `${t.stats.ordersOpened}: ${formatInteger(aggregate.orders_opened)}`,
        },
    ];

    const groupColumns = [
        { key: 'name', label: t.columns.name },
        { key: 'leads_total', label: t.columns.leads, render: (value) => formatInteger(value) },
        { key: 'deposits_total', label: t.columns.deposits, render: (value) => formatMoney(value) },
        { key: 'withdrawals_total', label: t.columns.withdrawals, render: (value) => formatMoney(value) },
        { key: 'bonus_total', label: t.columns.bonus, render: (value) => formatMoney(value) },
        { key: 'net_deposit', label: t.columns.net, render: (value) => formatMoney(value) },
        { key: 'comments_count', label: t.columns.comments, render: (value) => formatInteger(value) },
        { key: 'status_changes_count', label: t.columns.statusChanges, render: (value) => formatInteger(value) },
        { key: 'orders_opened', label: t.columns.opened, render: (value) => formatInteger(value) },
        { key: 'orders_closed', label: t.columns.closed, render: (value) => formatInteger(value) },
    ];

    return (
        <>
            <div className={styles.summaryGrid}>
                {summaryCards.map((card) => (
                    <SummaryCard key={card.title} {...card} theme={theme} />
                ))}
            </div>

            <div className={styles.statsRow}>
                <StatChip label={t.stats.deposits} value={formatMoney(aggregate.deposits_total)} tone="chipGreen" />
                <StatChip label={t.stats.withdrawals} value={formatMoney(aggregate.withdrawals_total)} tone="chipRed" />
                <StatChip label={t.stats.comments} value={formatInteger(aggregate.comments_count)} tone="chipBlue" />
                <StatChip label={t.stats.statusChanges} value={formatInteger(aggregate.status_changes_count)} tone="chipPurple" />
                <StatChip label={t.stats.ordersOpened} value={formatInteger(aggregate.orders_opened)} tone="chipYellow" />
            </div>

            <div className={styles.chartGrid}>
                <ChartShell title={t.charts.moneyCompare} subtitle={t.dateRange} theme={theme}>
                    <TimelineCompareChart
                        timeline={groupCharts?.deposits_by_day ?? []}
                        emptyLabel={t.empty}
                        theme={theme}
                        valueFormatter={formatMoney}
                    />
                </ChartShell>

                <ChartShell title={t.charts.depositShare} subtitle={t.scope} theme={theme}>
                    <ShareDonutChart
                        rows={rows}
                        emptyLabel={t.empty}
                        labelKey="name"
                        valueKey="deposits_total"
                        totalLabel={t.stats.deposits}
                    />
                </ChartShell>

                <ChartShell title={t.charts.activityCompare} subtitle={t.dateRange} theme={theme}>
                    <TimelineCompareChart
                        timeline={groupCharts?.status_changes_by_day ?? []}
                        emptyLabel={t.empty}
                        theme={theme}
                        valueFormatter={formatInteger}
                    />
                </ChartShell>

                <ChartShell title={tableTitle} subtitle={tableHint} theme={theme}>
                    <TimelineCompareChart
                        timeline={groupCharts?.orders_opened_by_day ?? []}
                        emptyLabel={t.empty}
                        theme={theme}
                        valueFormatter={formatInteger}
                    />
                </ChartShell>
            </div>

            <div className={styles.singleTableGrid}>
                <DataTable
                    title={tableTitle}
                    hint={tableHint}
                    rows={rows}
                    columns={groupColumns}
                    theme={theme}
                    emptyLabel={t.empty}
                />
            </div>
        </>
    );
};

export const AnalyticsPage = () => {
    const { theme } = useContext(ThemeContext);
    const { langSelected } = useContext(LangContext);
    const t = translations[langSelected] || translations.ru;
    const isDark = theme === 'dark';
    const defaultFilters = useMemo(() => createDefaultFilters(), []);

    const [permissionsLoaded, setPermissionsLoaded] = useState(false);
    const [hasAccess, setHasAccess] = useState(false);
    const [activeTab, setActiveTab] = useState('leads');
    const [draftFilters, setDraftFilters] = useState(defaultFilters);
    const [analytics, setAnalytics] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [isAutoApplyEnabled, setIsAutoApplyEnabled] = useState(false);

    useEffect(() => {
        const bootstrap = async () => {
            try {
                const accountData = await getMyAccountApi();
                const permissionNamesList = Array.isArray(accountData?.permissions)
                    ? accountData.permissions.map((permission) => permission.name)
                    : [];
                const canView = permissionNamesList.includes(permissionNames.listLeads);

                setHasAccess(canView);

                if (!canView) {
                    setAnalytics(null);
                    return;
                }

                const response = await getLeadAnalyticsApi(defaultFilters);
                setAnalytics(response);
                setIsAutoApplyEnabled(canView);
            } catch (loadError) {
                console.error('Failed to load analytics page:', loadError);
                setError(t.loadError);
            } finally {
                setPermissionsLoaded(true);
                setIsLoading(false);
            }
        };

        bootstrap();
    }, [defaultFilters, t.loadError]);

    useEffect(() => {
        if (!isAutoApplyEnabled) {
            return undefined;
        }

        const timeoutId = setTimeout(async () => {
            setIsLoading(true);
            setError('');

            try {
                const response = await getLeadAnalyticsApi(draftFilters);
                setAnalytics(response);
            } catch (loadError) {
                console.error('Failed to refresh analytics:', loadError);
                setError(t.loadError);
            } finally {
                setIsLoading(false);
            }
        }, 250);

        return () => clearTimeout(timeoutId);
    }, [draftFilters, isAutoApplyEnabled, t.loadError]);

    const handleTextFilterChange = (filterName, value) => {
        setDraftFilters((prev) => ({ ...prev, [filterName]: value }));
    };

    const handleSelectorChange = (filterName, value) => {
        setDraftFilters((prev) => {
            if (filterName === 'managerId') {
                return {
                    ...prev,
                    managerId: value,
                    managerIds: value ? [value] : [],
                };
            }

            if (filterName === 'deskId') {
                return {
                    ...prev,
                    deskId: value,
                    deskIds: value ? [value] : [],
                };
            }

            if (filterName === 'managerIds') {
                return {
                    ...prev,
                    leadId: '',
                    managerId: '',
                    managerIds: Array.isArray(value) ? value : [],
                };
            }

            if (filterName === 'deskIds') {
                return {
                    ...prev,
                    leadId: '',
                    deskId: '',
                    deskIds: Array.isArray(value) ? value : [],
                };
            }

            return { ...prev, [filterName]: value };
        });
    };

    const handleTabChange = (tabKey) => {
        setActiveTab(tabKey);
        setDraftFilters((prev) => {
            if (tabKey === 'managers') {
                return {
                    ...prev,
                    leadId: '',
                    deskId: '',
                    deskIds: [],
                    managerIds: Array.isArray(prev.managerIds) && prev.managerIds.length > 0
                        ? prev.managerIds
                        : (prev.managerId ? [prev.managerId] : []),
                    managerId: '',
                };
            }

            if (tabKey === 'desks') {
                return {
                    ...prev,
                    leadId: '',
                    managerId: '',
                    managerIds: [],
                    deskIds: Array.isArray(prev.deskIds) && prev.deskIds.length > 0
                        ? prev.deskIds
                        : (prev.deskId ? [prev.deskId] : []),
                    deskId: '',
                };
            }

            return {
                ...prev,
                managerIds: prev.managerId ? [prev.managerId] : [],
                deskIds: prev.deskId ? [prev.deskId] : [],
            };
        });
    };

    const handleQuickRangeSelect = (presetKey) => {
        const presetRange = createPresetRange(presetKey);

        setDraftFilters((prev) => ({
            ...prev,
            ...presetRange,
        }));
    };

    const handleReset = () => {
        setError('');
        setActiveTab('leads');
        setDraftFilters({ ...defaultFilters });
    };

    if (!permissionsLoaded || isLoading) {
        return (
            <div className={styles.loadingBlock}>
                <LoadingCircle size={42} thickness={4} />
            </div>
        );
    }

    if (!hasAccess) {
        return <div className={styles.accessDenied}>{t.accessDenied}</div>;
    }

    const summary = analytics?.summary ?? {};
    const charts = analytics?.charts ?? {};
    const rankings = analytics?.rankings ?? { desks: [], managers: [], leads: [] };
    const groupCharts = analytics?.group_charts ?? { managers: {}, desks: {} };
    const filterOptions = analytics?.filters?.options ?? { leads: [], managers: [], desks: [] };

    const selectorOptions = {
        leads: toSelectorOptions(filterOptions.leads, t.all),
        managers: toSelectorOptions(filterOptions.managers, t.all),
        desks: toSelectorOptions(filterOptions.desks, t.all),
    };

    const renderQuickRanges = () => (
        <div className={styles.quickRangeRow}>
            <button type="button" className={styles.quickRangeButton} onClick={() => handleQuickRangeSelect('sevenDays')}>
                {t.quickRanges.sevenDays}
            </button>
            <button type="button" className={styles.quickRangeButton} onClick={() => handleQuickRangeSelect('oneMonth')}>
                {t.quickRanges.oneMonth}
            </button>
            <button type="button" className={styles.quickRangeButton} onClick={() => handleQuickRangeSelect('threeMonths')}>
                {t.quickRanges.threeMonths}
            </button>
        </div>
    );

    const renderFilters = () => {
        if (activeTab === 'managers') {
            return (
                <>
                    <div className={styles.field}>
                        <label className={styles.fieldLabel}>{t.manager}</label>
                        <MultiSelectDropdown
                            filterName="managerIds"
                            selectedValue={draftFilters.managerIds}
                            handleFilterChange={handleSelectorChange}
                            options={filterOptions.managers}
                            isSearchable
                        />
                    </div>
                    <div className={styles.field}>
                        <label className={styles.fieldLabel}>{t.from}</label>
                        <InputSelector
                            filterName="fromDate"
                            selectedValue={draftFilters.fromDate}
                            handleFilterChange={handleTextFilterChange}
                            type="date"
                        />
                    </div>
                    <div className={styles.field}>
                        <label className={styles.fieldLabel}>{t.to}</label>
                        <InputSelector
                            filterName="toDate"
                            selectedValue={draftFilters.toDate}
                            handleFilterChange={handleTextFilterChange}
                            type="date"
                        />
                    </div>
                    {renderQuickRanges()}
                </>
            );
        }

        if (activeTab === 'desks') {
            return (
                <>
                    <div className={styles.field}>
                        <label className={styles.fieldLabel}>{t.desk}</label>
                        <MultiSelectDropdown
                            filterName="deskIds"
                            selectedValue={draftFilters.deskIds}
                            handleFilterChange={handleSelectorChange}
                            options={filterOptions.desks}
                            isSearchable
                        />
                    </div>
                    <div className={styles.field}>
                        <label className={styles.fieldLabel}>{t.from}</label>
                        <InputSelector
                            filterName="fromDate"
                            selectedValue={draftFilters.fromDate}
                            handleFilterChange={handleTextFilterChange}
                            type="date"
                        />
                    </div>
                    <div className={styles.field}>
                        <label className={styles.fieldLabel}>{t.to}</label>
                        <InputSelector
                            filterName="toDate"
                            selectedValue={draftFilters.toDate}
                            handleFilterChange={handleTextFilterChange}
                            type="date"
                        />
                    </div>
                    {renderQuickRanges()}
                </>
            );
        }

        return (
            <>
                <div className={styles.field}>
                    <label className={styles.fieldLabel}>{t.lead}</label>
                    <DropdownSelector
                        filterName="leadId"
                        selectedValue={draftFilters.leadId}
                        handleFilterChange={handleSelectorChange}
                        options={selectorOptions.leads}
                    />
                </div>
                <div className={styles.field}>
                    <label className={styles.fieldLabel}>{t.manager}</label>
                    <DropdownSelector
                        filterName="managerId"
                        selectedValue={draftFilters.managerId}
                        handleFilterChange={handleSelectorChange}
                        options={selectorOptions.managers}
                    />
                </div>
                <div className={styles.field}>
                    <label className={styles.fieldLabel}>{t.desk}</label>
                    <DropdownSelector
                        filterName="deskId"
                        selectedValue={draftFilters.deskId}
                        handleFilterChange={handleSelectorChange}
                        options={selectorOptions.desks}
                    />
                </div>
                <div className={styles.field}>
                    <label className={styles.fieldLabel}>{t.from}</label>
                    <InputSelector
                        filterName="fromDate"
                        selectedValue={draftFilters.fromDate}
                        handleFilterChange={handleTextFilterChange}
                        type="date"
                    />
                </div>
                <div className={styles.field}>
                    <label className={styles.fieldLabel}>{t.to}</label>
                    <InputSelector
                        filterName="toDate"
                        selectedValue={draftFilters.toDate}
                        handleFilterChange={handleTextFilterChange}
                        type="date"
                    />
                </div>
                {renderQuickRanges()}
            </>
        );
    };

    return (
        <div className={styles.container}>
            <div className={`${styles.content} ${isDark ? styles.darkContent : ''}`}>
                <div className={styles.topBar}>
                    <Title h2={t.title} />
                </div>

                {error ? <div className={styles.errorBanner}>{error}</div> : null}

                <div className={`${styles.tabNav} ${isDark ? styles.darkTabNav : ''}`}>
                    {['leads', 'managers', 'desks'].map((tabKey) => (
                        <button
                            key={tabKey}
                            type="button"
                            className={`${styles.tabButton} ${activeTab === tabKey ? styles.activeTabButton : ''}`}
                            onClick={() => handleTabChange(tabKey)}
                        >
                            {t.tabs[tabKey]}
                        </button>
                    ))}
                </div>

                <section className={`${styles.filterPanel} ${isDark ? styles.darkPanel : ''}`}>
                    <div className={styles.filterPanelHeader}>
                        <div>
                            <h3>{t.currentSlice}</h3>
                            <p>{t.filtersHint}</p>
                        </div>
                        <div className={styles.filterActions}>
                            <button className={styles.secondaryButton} type="button" onClick={handleReset}>
                                <RestartAltOutlinedIcon fontSize="small" />
                                {t.reset}
                            </button>
                        </div>
                    </div>

                    <div className={styles.filterGrid}>
                        {renderFilters()}
                    </div>
                </section>

                {activeTab === 'leads' ? (
                    <LeadsTab summary={summary} charts={charts} rankings={rankings} theme={theme} t={t} />
                ) : null}

                {activeTab === 'managers' ? (
                    <GroupTab
                        rows={rankings.managers ?? []}
                        groupCharts={groupCharts.managers ?? {}}
                        theme={theme}
                        t={t}
                        type="manager"
                        tableTitle={t.tables.managers}
                        tableHint={t.tables.managersHint}
                    />
                ) : null}

                {activeTab === 'desks' ? (
                    <GroupTab
                        rows={rankings.desks ?? []}
                        groupCharts={groupCharts.desks ?? {}}
                        theme={theme}
                        t={t}
                        type="desk"
                        tableTitle={t.tables.desks}
                        tableHint={t.tables.desksHint}
                    />
                ) : null}
            </div>
        </div>
    );
};
